import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  formatInteger,
  formatShortenedTimespan,
  FormattedValueWithUnit,
  getCurrencySymbolSettings,
} from '../../common/formatHelper';
import { getDefaultMeasure, getMeasureTitle } from '../../common/measureHelper';
import useFormatter from '../../common/useFormatter';
import { warehouseSelected } from '../../store/warehouse.state';
import {
  ActualityAggregatedHqExtendedDataRow,
  ActualityAggregatedHqExtendedStatsDataColumn,
} from '../store/actuality.kpi.types';
import { getActualityAggregationMetricDescriptorsMap } from '../store/feedMetrics';
import useActualityAggregationMetricDescriptors from './useActualityAggregationMetricDescriptors';

const colors = TwTheme.extend.colors;

export type PieChartMetricType =
  | 'share'
  | 'events'
  | 'duration'
  | 'cost'
  | 'distance'
  | 'advanced';

export interface MetricConfig {
  id: PieChartMetricType;
  label: string;
  title?: string;
  icon: string;
  getTotalLabel: (...args: any[]) => string;
  getValue: (...args: any[]) => number;
  getTotalValue: (...args: any[]) => number;
  formatValue: (...args: any[]) => FormattedValueWithUnit;
}

const createMetricConfigs = (): Record<PieChartMetricType, MetricConfig> => ({
  share: {
    id: 'share',
    label: 'Share',
    icon: '%',
    getTotalLabel: t => t`Total Share`,
    getValue: (row, totalEvents = 0) =>
      totalEvents > 0 ? (row.eventsCount / totalEvents) * 100 : 0,
    getTotalValue: () => 100, // Always 100% for share
    formatValue: (value, formatter) => formatter.formatShare(value / 100),
  },
  events: {
    id: 'events',
    label: 'Events',
    icon: 'events',
    getTotalLabel: t => t`Total Events`,
    getValue: row => row.eventsCount || 0,
    getTotalValue: data => _.sumBy(data, 'eventsCount') || 0,
    formatValue: (value, formatter, wh, t) => ({
      value: formatInteger(value),
      fullString: `${formatInteger(value)}`,
      unit: t`events`,
      raw: value,
      unitCode: 'events',
    }),
  },
  duration: {
    id: 'duration',
    label: 'Duration',
    icon: 'h',
    getTotalLabel: t => t`Total Duration`,
    getValue: row => row.analyzedDuration || 0,
    getTotalValue: data => _.sumBy(data, 'analyzedDuration') || 0,
    formatValue: value => {
      const formattedDuration = formatShortenedTimespan(value * 1000, null);
      return {
        value: formattedDuration.split(' ')[0],
        fullString: formattedDuration,
        unit: formattedDuration.split(' ')[1] || 's',
        raw: value,
        unitCode: 'seconds',
      };
    },
  },
  cost: {
    id: 'cost',
    label: 'Cost',
    icon: 'cost',
    getTotalLabel: t => t`Total Cost`,
    getValue: row => row.analyzedCost || 0,
    getTotalValue: data => _.sumBy(data, 'analyzedCost') || 0,
    formatValue: (value, formatter, wh) => {
      const systemCurrency = getCurrencySymbolSettings(wh.currency).symbol;
      return {
        value: value.toFixed(2),
        fullString: `${value.toFixed(2)} ${systemCurrency}`,
        unit: systemCurrency,
        raw: value,
        unitCode: 'currency',
      };
    },
  },
  distance: {
    id: 'distance',
    label: 'Distance',
    icon: 'distance',
    getTotalLabel: t => t`Total Distance`,
    getValue: row =>
      (row.analyzedDistance || 0) +
      (row.analyzedDistanceTravellingStart || 0) +
      (row.analyzedDistanceTravellingEnd || 0) +
      (row.analyzedDistanceTravellingInternal || 0) +
      (row.analyzedDistanceTravellingVertical || 0),
    getTotalValue: (data: ActualityAggregatedHqExtendedDataRow[]) =>
      _.sumBy(
        data,
        (row: ActualityAggregatedHqExtendedDataRow) =>
          (row.analyzedDistance || 0) +
          (row.analyzedDistanceTravellingStart || 0) +
          (row.analyzedDistanceTravellingEnd || 0) +
          (row.analyzedDistanceTravellingInternal || 0) +
          (row.analyzedDistanceTravellingVertical || 0),
      ) || 0,
    formatValue: (value, formatter, wh, t) => {
      const { t: tMeasures } = useTranslation('measures');
      const sizeMeasureValue = getMeasureTitle(
        getDefaultMeasure(wh.measurementSystem, 'distance'),
        tMeasures,
      );
      return {
        value: value.toFixed(1),
        fullString: `${value.toFixed(1)} ${sizeMeasureValue}`,
        unit: sizeMeasureValue,
        raw: value,
        unitCode: 'distance',
      };
    },
  },
  advanced: {
    id: 'advanced',
    label: 'Advanced',
    icon: '...',
    getTotalLabel: (t, aggMetricDescriptors, selectedMetric) =>
      aggMetricDescriptors?.[selectedMetric]?.title || t`Total`,
    getValue: (row, totalEvents, selectedMetric) =>
      _.get(row, selectedMetric) || 0,
    getTotalValue: (data, totalEvents, selectedMetric) =>
      _.sumBy(data, row => _.get(row, selectedMetric) || 0) || 0,
    formatValue: (
      value,
      formatter,
      wh,
      t,
      aggMetricDescriptors,
      selectedMetric,
    ) => {
      const metricDescriptor = aggMetricDescriptors?.[selectedMetric];
      if (metricDescriptor && _.isNumber(value)) {
        const formatted = metricDescriptor.getFormatted(value);
        return {
          value: formatted,
          fullString:
            formatted +
            (metricDescriptor.unitOfMeasure
              ? ` ${metricDescriptor.unitOfMeasure}`
              : ''),
          unit: metricDescriptor.unitOfMeasure || '',
          raw: value,
          unitCode: 'metric',
        };
      }
      return {
        value: value.toString(),
        fullString: value.toString(),
        unit: '',
        raw: value,
        unitCode: 'metric',
      };
    },
  },
});

export function usePieChartMetrics(
  data: ActualityAggregatedHqExtendedDataRow[],
) {
  const { t } = useTranslation('feed');
  const { t: tMeasures } = useTranslation('measures');
  const wh = useRecoilValue(warehouseSelected);
  const formatter = useFormatter();
  const aggMetricDescriptors = useActualityAggregationMetricDescriptors();
  const metricDescriptorsMap = getActualityAggregationMetricDescriptorsMap(
    t,
    formatter,
  );

  const [currentMetric, setCurrentMetric] =
    useState<PieChartMetricType>('share');
  const [selectedAdvancedMetric, setSelectedAdvancedMetric] =
    useState<ActualityAggregatedHqExtendedStatsDataColumn>('eventsCount');

  // Get size measure value once at hook level
  const sizeMeasureValue = getMeasureTitle(
    getDefaultMeasure(wh.measurementSystem, 'distance'),
    tMeasures,
  );

  const systemCurrency = getCurrencySymbolSettings(wh.currency).symbol;

  const metricConfigs: Record<PieChartMetricType, MetricConfig> = {
    share: {
      id: 'share',
      label: t('Share'),
      title: t('Share'),
      icon: '%',
      getTotalLabel: (t: any) => t`Total Share`,
      getValue: (row: ActualityAggregatedHqExtendedDataRow, totalEvents = 0) =>
        totalEvents > 0 ? (row.eventsCount / totalEvents) * 100 : 0,
      getTotalValue: () => 100, // Always 100% for share
      formatValue: (value: number, formatter: any) =>
        formatter.formatShare(value / 100),
    },
    events: {
      id: 'events',
      label: metricDescriptorsMap['eventsCount']?.legend || t('Events'),
      title: metricDescriptorsMap['eventsCount']?.title || t('Events'),
      icon: 'events',
      getTotalLabel: (t: any) => t`Total Events`,
      getValue: (row: ActualityAggregatedHqExtendedDataRow) =>
        row.eventsCount || 0,
      getTotalValue: (data: ActualityAggregatedHqExtendedDataRow[]) =>
        _.sumBy(data, 'eventsCount') || 0,
      formatValue: (value: number, formatter: any, wh: any, t: any) => ({
        value: formatInteger(value),
        fullString: `${formatInteger(value)}`,
        unit: t`events`,
        raw: value,
        unitCode: 'events',
      }),
    },
    duration: {
      id: 'duration',
      label: metricDescriptorsMap['analyzedDuration']?.legend || t('Duration'),
      title: metricDescriptorsMap['analyzedDuration']?.title || t('Duration'),
      icon: 'h',
      getTotalLabel: (t: any) => t`Total Duration`,
      getValue: (row: ActualityAggregatedHqExtendedDataRow) =>
        row.analyzedDuration || 0,
      getTotalValue: (data: ActualityAggregatedHqExtendedDataRow[]) =>
        _.sumBy(data, 'analyzedDuration') || 0,
      formatValue: (value: number) => {
        const formattedDuration = formatShortenedTimespan(value * 1000, null);
        return {
          value: formattedDuration.split(' ')[0],
          fullString: formattedDuration,
          unit: formattedDuration.split(' ')[1] || 's',
          raw: value,
          unitCode: 'seconds',
        };
      },
    },
    cost: {
      id: 'cost',
      label: metricDescriptorsMap['analyzedCost']?.legend || t('Cost'),
      title: metricDescriptorsMap['analyzedCost']?.title || t('Cost'),
      icon: systemCurrency,
      getTotalLabel: (t: any) => t`Total Cost`,
      getValue: (row: ActualityAggregatedHqExtendedDataRow) =>
        row.analyzedCost || 0,
      getTotalValue: (data: ActualityAggregatedHqExtendedDataRow[]) =>
        _.sumBy(data, 'analyzedCost') || 0,
      formatValue: (value: number, formatter: any, wh: any) => {
        const systemCurrency = getCurrencySymbolSettings(wh.currency).symbol;
        return {
          value: value.toFixed(2),
          fullString: `${value.toFixed(2)} ${systemCurrency}`,
          unit: systemCurrency,
          raw: value,
          unitCode: 'currency',
        };
      },
    },
    distance: {
      id: 'distance',
      label: metricDescriptorsMap['analyzedDistance']?.legend || t('Distance'),
      title: metricDescriptorsMap['analyzedDistance']?.title || t('Distance'),
      icon: sizeMeasureValue,
      getTotalLabel: (t: any) => t`Total Distance`,
      getValue: (row: ActualityAggregatedHqExtendedDataRow) =>
        (row.analyzedDistance || 0) +
        (row.analyzedDistanceTravellingStart || 0) +
        (row.analyzedDistanceTravellingEnd || 0) +
        (row.analyzedDistanceTravellingInternal || 0) +
        (row.analyzedDistanceTravellingVertical || 0),
      getTotalValue: (data: ActualityAggregatedHqExtendedDataRow[]) =>
        _.sumBy(
          data,
          (row: ActualityAggregatedHqExtendedDataRow) =>
            (row.analyzedDistance || 0) +
            (row.analyzedDistanceTravellingStart || 0) +
            (row.analyzedDistanceTravellingEnd || 0) +
            (row.analyzedDistanceTravellingInternal || 0) +
            (row.analyzedDistanceTravellingVertical || 0),
        ) || 0,
      formatValue: (value: number) => ({
        value: value.toFixed(1),
        fullString: `${value.toFixed(1)} ${sizeMeasureValue}`,
        unit: sizeMeasureValue,
        raw: value,
        unitCode: 'distance',
      }),
    },
    advanced: {
      id: 'advanced',
      label: t('Advanced'),
      title: t('Advanced'),
      icon: '...',
      getTotalLabel: (t: any, aggMetricDescriptors: any, selectedMetric: any) =>
        aggMetricDescriptors?.[selectedMetric]?.title || t`Total`,
      getValue: (
        row: ActualityAggregatedHqExtendedDataRow,
        totalEvents: number,
        selectedMetric: ActualityAggregatedHqExtendedStatsDataColumn,
      ) => _.get(row, selectedMetric) || 0,
      getTotalValue: (
        data: ActualityAggregatedHqExtendedDataRow[],
        totalEvents: number,
        selectedMetric: ActualityAggregatedHqExtendedStatsDataColumn,
      ) =>
        _.sumBy(
          data,
          (row: ActualityAggregatedHqExtendedDataRow) =>
            _.get(row, selectedMetric) || 0,
        ) || 0,
      formatValue: (
        value: number,
        formatter: any,
        wh: any,
        t: any,
        aggMetricDescriptors: any,
        selectedMetric: ActualityAggregatedHqExtendedStatsDataColumn,
      ) => {
        const metricDescriptor = aggMetricDescriptors?.[selectedMetric];
        if (metricDescriptor && _.isNumber(value)) {
          const formatted = metricDescriptor.getFormatted(value);
          return {
            value: formatted,
            fullString:
              formatted +
              (metricDescriptor.unitOfMeasure
                ? ` ${metricDescriptor.unitOfMeasure}`
                : ''),
            unit: metricDescriptor.unitOfMeasure || '',
            raw: value,
            unitCode: 'metric',
          };
        }
        return {
          value: value.toString(),
          fullString: value.toString(),
          unit: '',
          raw: value,
          unitCode: 'metric',
        };
      },
    },
  };

  const totalEventsCount = _.sumBy(data, 'eventsCount') || 0;

  const getCurrentConfig = () => metricConfigs[currentMetric];

  const getMetricValue = (row: ActualityAggregatedHqExtendedDataRow) => {
    const config = getCurrentConfig();
    if (currentMetric === 'share') {
      return config.getValue(row, totalEventsCount);
    }
    if (currentMetric === 'advanced') {
      return config.getValue(row, totalEventsCount, selectedAdvancedMetric);
    }
    return config.getValue(row);
  };

  const getTotalValue = () => {
    const config = getCurrentConfig();
    if (currentMetric === 'advanced') {
      return config.getTotalValue(
        data,
        totalEventsCount,
        selectedAdvancedMetric,
      );
    }
    return config.getTotalValue(data, totalEventsCount);
  };

  const getTotalLabel = () => {
    const config = getCurrentConfig();
    if (currentMetric === 'advanced') {
      return config.getTotalLabel(
        t,
        aggMetricDescriptors,
        selectedAdvancedMetric,
      );
    }
    return config.getTotalLabel(t);
  };

  const getFormattedValue = (value: number): FormattedValueWithUnit => {
    const config = getCurrentConfig();
    return config.formatValue(
      value,
      formatter,
      wh,
      t,
      aggMetricDescriptors,
      selectedAdvancedMetric,
    );
  };

  const getMetricOptions = () =>
    Object.values(metricConfigs).map(config => ({
      label: config.title || config.label,
      icon: config.icon,
    }));

  return {
    currentMetric,
    setCurrentMetric,
    selectedAdvancedMetric,
    setSelectedAdvancedMetric,
    getMetricValue,
    getTotalValue,
    getTotalLabel,
    getFormattedValue,
    getMetricOptions,
    metricConfigs,
    totalEventsCount,
  };
}
